import React, { useState } from 'react';
import './BollingerExitConditionsBar.css';

export interface VwapExitConditions {
  currentPrice: number;
  /** Stop-loss TRIGGER price (mean_reversion_cycle.stop_loss_price). */
  stopLoss?: number;
  /** Stop-loss LIMIT price actually sitting on the broker (coinbase_trade_order_log.limit_price).
   *  Shown as subtext under the trigger so the operator can reconcile with their Coinbase view. */
  stopLossLimit?: number;
  entryPrice?: number;
  vwapTarget?: number;
  isPartialExit: boolean;
}

export interface VwapExitConditionsBarProps {
  exitConditions?: VwapExitConditions;
  isLoading?: boolean;
  className?: string;
}

/**
 * VwapExitConditionsBar — visual indicator of exit proximity for VWAP_REVERSION
 * trade groups in IN_POSITION / PARTIAL_EXIT state. Mirrors BollingerExitConditionsBar.
 *
 * Conditions surfaced:
 *   - Stop Loss (critical, color-coded red as price approaches)
 *   - Entry Price (informational reference point)
 *   - VWAP Target (the mean-revert exit trigger — fires when price ≥ VWAP)
 *
 * Sibling component: BollingerExitConditionsBar. Reuses the same CSS module so the
 * visual chrome stays identical across strategies.
 */
const VwapExitConditionsBar: React.FC<VwapExitConditionsBarProps> = ({
  exitConditions,
  isLoading = false,
  className = '',
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  if (isLoading) {
    return (
      <div className={`bollinger-exit-conditions-bar ${className}`}>
        <div className="exit-targets-display loading">
          <div className="exit-target-card skeleton"><div className="skeleton-shimmer" /></div>
          <div className="exit-target-card skeleton"><div className="skeleton-shimmer" /></div>
          <div className="exit-target-card skeleton"><div className="skeleton-shimmer" /></div>
        </div>
      </div>
    );
  }

  if (!exitConditions) {
    return null;
  }

  const { currentPrice, stopLoss, stopLossLimit, entryPrice, vwapTarget, isPartialExit } = exitConditions;

  const calculateProximity = (targetPrice: number | undefined): number => {
    if (!targetPrice || !currentPrice || currentPrice <= 0) return 0;
    return ((targetPrice - currentPrice) / currentPrice) * 100;
  };

  const stopLossProximity = calculateProximity(stopLoss);
  const entryProximity = calculateProximity(entryPrice);
  const vwapProximity = calculateProximity(vwapTarget);

  const getStatus = (proximity: number): 'at-target' | 'near' | 'far' => {
    const absProximity = Math.abs(proximity);
    if (absProximity < 0.5) return 'at-target';
    if (absProximity < 2.0) return 'near';
    return 'far';
  };

  const conditions: Array<{ label: string; value: number; proximity: number; status: 'at-target' | 'near' | 'far'; critical: boolean }> = [];

  if (stopLoss) {
    conditions.push({
      label: 'Stop Loss',
      value: stopLoss,
      proximity: stopLossProximity,
      status: getStatus(stopLossProximity),
      critical: stopLossProximity > -2.0,
    });
  }

  if (entryPrice) {
    conditions.push({
      label: 'Entry Price',
      value: entryPrice,
      proximity: entryProximity,
      status: getStatus(entryProximity),
      critical: false,
    });
  }

  if (vwapTarget) {
    conditions.push({
      label: isPartialExit ? 'VWAP Target (Remainder)' : 'VWAP Target',
      value: vwapTarget,
      proximity: vwapProximity,
      status: getStatus(vwapProximity),
      critical: false,
    });
  }

  const nextTarget = conditions
    .filter(c => c.proximity > 0 && !c.critical)
    .sort((a, b) => a.proximity - b.proximity)[0];

  const formatPrice = (price: number): string => {
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const getSummaryText = (): string => {
    if (nextTarget) {
      return `Next: ${nextTarget.label} at ${formatPrice(nextTarget.value)} (+${nextTarget.proximity.toFixed(2)}%)`;
    }
    const stopLossTarget = conditions.find(c => c.critical && c.proximity < 0);
    if (stopLossTarget) {
      return `Stop Loss at ${formatPrice(stopLossTarget.value)} (${stopLossTarget.proximity.toFixed(2)}%)`;
    }
    return 'Monitoring exit targets';
  };

  return (
    <div className={`bollinger-exit-conditions-bar ${className}`}>
      <div className="exit-targets-summary">{getSummaryText()}</div>
      <div className="exit-targets-display">
        {conditions.map((condition, index) => {
          const { label, value, proximity, status, critical } = condition;
          const isNext = nextTarget && nextTarget.value === value;
          let cardClass = `exit-target-card ${status}`;
          if (critical) cardClass += ' critical';
          if (isNext) cardClass += ' next-target';

          let color = '#A78BFA';
          if (critical) color = '#ef4444';
          else if (status === 'at-target') color = '#10b981';
          else if (status === 'near') color = '#f59e0b';

          return (
            <div
              key={index}
              className={cardClass}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
              role="status"
              tabIndex={0}
            >
              <div className="target-label">{label}</div>
              <div className="target-price" style={{ color }}>{formatPrice(value)}</div>
              {label === 'Stop Loss' && stopLossLimit != null && (
                <div className="target-distance" style={{ fontSize: '0.85em', opacity: 0.8 }}>
                  Limit: {formatPrice(stopLossLimit)}
                </div>
              )}
              <div className="target-distance">
                {proximity > 0 ? '+' : ''}{proximity.toFixed(2)}%
                {isNext && ' (Next)'}
              </div>
              {hoveredIndex === index && (
                <div className="exit-condition-tooltip" role="tooltip">
                  <div className="tooltip-header">{label}</div>
                  <div className="tooltip-detail">Price: {formatPrice(value)}</div>
                  <div className="tooltip-proximity">
                    Distance: {proximity > 0 ? '+' : ''}{proximity.toFixed(2)}%
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VwapExitConditionsBar;
